<template>
    <div class="container">
        <div v-if="activeTask" class="alert alert-info py-2 my-3">
            {{ $t('home.workOrderShort') }}
            <router-link :to="`/tasks/${activeTask.task_id}`" class="fw-bold">{{ activeTask.task_id }}</router-link>
            {{ activeTypeLabel }} - {{ elapsedLabel }}
        </div>

        <HomeTaskCount :taskCount="taskCount" :headline="$t('taskViews.unfinished')" :description="$t('home.unfinishedDesc')"
            link="/tasks-unfinished/" numberClass="div-green" />

        <HomeTaskCount v-if="taskCount > 0" :taskCount="taskCount" :headline="$t('home.unfinishedGrouped')"
            :description="$t('home.unfinishedGroupedDesc')" link="/tasks-unfinished-grouped/"
            numberClass="div-green" />

<!--         <HomeTaskCount v-if="taskMaintenanceCount > 0" :taskCount="taskMaintenanceCount" headline="Hooldused"
            description="Hooldustööd, mis on määratud Sinule." link="/tasks-maintenance/" numberClass="div-green" /> -->

        <HomeTaskCount v-if="taskMaintenanceCount > 0" :taskCount="taskMaintenanceCount" :headline="$t('home.maintenanceGrouped')"
            :description="$t('home.maintenanceGroupedDesc')" link="/tasks-maintenance-grouped/" numberClass="div-green" />

<!--         <HomeTaskCount v-if="taskOrderCount > 0" :taskCount="taskOrderCount" headline="Rikked"
            description="Rikketööd, mis on määratud Sinule." link="/tasks-order/" numberClass="div-green" /> -->

        <HomeTaskCount v-if="taskOrderCount > 0" :taskCount="taskOrderCount" :headline="$t('home.orderGrouped')"
            :description="$t('home.orderGroupedDesc')" link="/tasks-order-grouped/" numberClass="div-green" />

<!--         <HomeTaskCount v-if="taskInstallationCount > 0" :taskCount="taskInstallationCount" headline="Paigaldused"
            description="Paigaldustööd, mis on määratud Sinule." link="/tasks-installation/" numberClass="div-green" /> -->

        <HomeTaskCount v-if="taskInstallationCount > 0" :taskCount="taskInstallationCount" :headline="$t('home.installationGrouped')"
            :description="$t('home.installationGroupedDesc')" link="/tasks-installation-grouped/" numberClass="div-green" />

        <HomeTaskCount v-if="taskAgreedCount > 0" :taskCount="taskAgreedCount" :headline="$t('taskViews.agreed')"
            :description="$t('home.agreedDesc')" link="/tasks-agreed/" numberClass="div-red" />

        <HomeTaskCount v-if="taskOverdueCount > 0" :taskCount="taskOverdueCount" :headline="$t('taskViews.overdue')"
            :description="$t('home.overdueDesc')" link="/tasks-overdue/" numberClass="div-red" />

        <HomeTaskCount v-if="taskInProgressCount > 0" :taskCount="taskInProgressCount" :headline="$t('taskViews.inProgress')"
            :description="$t('home.inProgressDesc')" link="/tasks-inprogress/" numberClass="div-yellow" />

        <HomeTaskCount v-if="taskCriticalCount > 0" :taskCount="taskCriticalCount"
            :headline="$t('taskViews.critical')" :description="$t('home.criticalDesc')"
            link="/tasks-critical/" numberClass="div-red" />

        <HomeTaskCount v-if="taskTransmitterRelatedCount > 0" :taskCount="taskTransmitterRelatedCount"
            :headline="$t('taskViews.transmitterRelated')" :description="$t('home.transmitterRelatedDesc')"
            link="/tasks-transmitter-related/" numberClass="div-green" />

        <HomeTaskCount v-if="taskOpenCount !== null && taskOpenCount > 0" :taskCount="taskOpenCount"
            :headline="$t('taskViews.open')" :description="$t('home.openDesc')" link="/tasks-open/"
            numberClass="div-purple" />

        <OnCallList v-if="can('Priv_AppCalendar')" />
    </div>
</template>

<script>
import axios from '@/services/axios';
import HomeTaskCount from '../components/HomeTaskCount.vue';
import OnCallList from '@/components/OnCallList.vue';
import { can as canPerm } from '@/composables/usePerms';

export default {
    name: 'HomeView',
    components: { HomeTaskCount, OnCallList },

    data() {
        return {
            taskMaintenanceCount: 0,
            taskOrderCount: 0,
            taskInstallationCount: 0,
            taskOverdueCount: 0,
            taskCriticalCount: 0,
            taskTransmitterRelatedCount: 0,
            taskTodayCount: 0,
            taskTomorrowCount: 0,
            taskInProgressCount: 0,
            taskCount: 0,
            taskOpenCount: null,
            taskAgreedCount: 0,
            activeTask: null,
            statusReceivedAt: 0, // Date.now() when activeTask was last fetched
            nowTs: Date.now(),   // ticked every second to recompute elapsed time

            pollId: null,
            pollIntervalMs: 60_000, // 1 minute
            tickId: null,
        };
    },

    computed: {
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        },
        activeTypeLabel() {
            if (!this.activeTask) return '';
            // type_id 0 = driving, 1/2 = working
            return this.activeTask.type_id == 0 ? this.$t('home.statusDrive') : this.$t('home.statusWork');
        },
        elapsedLabel() {
            if (!this.activeTask) return '';
            // Server-authoritative seconds + real time elapsed since we received it.
            // nowTs uses Date.now() (wall clock), so it stays correct across sleep.
            const base = Number(this.activeTask.elapsed_seconds) || 0;
            const extra = Math.max(0, Math.floor((this.nowTs - this.statusReceivedAt) / 1000));
            const total = base + extra;
            const pad = (n) => String(n).padStart(2, '0');
            return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
        }
    },

    async mounted() {
        localStorage.removeItem('taskListFilterKeys');
        document.body.style.backgroundColor = 'white';

        // Fetch once when user lands on the page
        await Promise.all([this.fetchTasks(), this.fetchUserStatus()]);

        // Start polling and the elapsed-time ticker only if the tab is visible
        if (!document.hidden) {
            this.startPolling();
            this.startTick();
        }

        // React to tab visibility changes
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    },

    beforeUnmount() {
        this.stopPolling();
        this.stopTick();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.body.style.backgroundColor = '';
    },

    methods: {
        can(perm) {
            return canPerm(perm);
        },
        async fetchTasks() {
            try {
                if (!this.currentUser?.id) return;

                const { data } = await axios.get(`tasks/summary?users=${this.currentUser.id}`);
                const s = data.data;

                this.taskMaintenanceCount = s.maintenance;
                this.taskOrderCount = s.order;
                this.taskInstallationCount = s.installation;
                this.taskOverdueCount = s.overdue;
                this.taskCriticalCount = s.critical;
                this.taskTransmitterRelatedCount = s.transmitter_related;
                this.taskTodayCount = s.today;
                this.taskTomorrowCount = s.tomorrow;
                this.taskInProgressCount = s.in_progress;
                this.taskCount = s.unfinished;
                this.taskAgreedCount = s.agreed;
                this.taskOpenCount = s.open;
            } catch (error) {
                console.error('Error fetching tasks:', error);
            }
        },

        async fetchUserStatus() {
            try {
                if (!this.currentUser?.id) return;

                const { data } = await axios.get('users/user_status');
                // data.data is the active task object, or null when nothing is active.
                this.activeTask = data.data;
                // Anchor the server's elapsed_seconds to the local wall clock so the
                // ticking timer stays correct between polls and across sleep/resume.
                this.statusReceivedAt = Date.now();
                this.nowTs = this.statusReceivedAt;
            } catch (error) {
                console.error('Error fetching user status:', error);
            }
        },

        startTick() {
            this.stopTick();
            this.tickId = setInterval(() => {
                this.nowTs = Date.now();
            }, 1000);
        },

        stopTick() {
            if (this.tickId) {
                clearInterval(this.tickId);
                this.tickId = null;
            }
        },

        startPolling() {
            this.stopPolling(); // avoid duplicates
            this.pollId = setInterval(() => {
                if (!document.hidden) {
                    this.fetchTasks();
                    this.fetchUserStatus();
                }
            }, this.pollIntervalMs);
        },

        stopPolling() {
            if (this.pollId) {
                clearInterval(this.pollId);
                this.pollId = null;
            }
        },

        onVisibilityChange() {
            if (document.hidden) {
                this.stopPolling(); // tab hidden → stop
                this.stopTick();
            } else {
                // tab visible → resume polling and ticking, and immediately
                // re-sync the active-work-order timer with the server.
                this.startPolling();
                this.startTick();
                this.nowTs = Date.now();
                this.fetchUserStatus();
            }
        }
    }
};
</script>
<style scoped>
.frame .t-ext {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    width: 225px;
}

.frame .text-wrapper {
    color: #2f174c;
    font-family: "Fira Sans-Medium", Helvetica;
    font-size: 22px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 28.6px;
    margin-top: -1px;
    position: relative;
    white-space: nowrap;
    width: fit-content;
}

.frame .label-text {
    color: #333f4c;
    font-family: "Fira Sans-Regular", Helvetica;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: normal;
    position: relative;
    white-space: nowrap;
    width: fit-content;
}

.frame .right-arrow {
    height: 32px;
    margin-left: -12px;
    position: relative;
    width: 32px;
}
</style>